/*
 *   File : login.js
 *   Author : https://evoqins.com
 *   Description : login page
 *   Integrations : js-cookie,react-toastify
 *   Version : 1.0.0
*/

import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { isValidPhoneNumber } from "react-phone-number-input";
import { toast } from "react-toastify";
// import AppleSignin from 'react-apple-signin-auth';
import { GoogleAuthProvider, OAuthProvider, signInWithPopup, signOut } from "firebase/auth";
import { auth } from "./firebase";


//custom components
import { OTPInput, CustomPhoneInput, RadioGroup, CustomTextInput } from "../../Component/Form-Elements";
import { PrimaryButton } from "../../Component/Buttons";
import APIService from "../../Service/service";
import { Footer } from "../../Component/Footer";
import { Header } from "../../Component/Navigation";
import { Icon } from "../../Component/Icon"
import { AuthSlider } from "../../Component/Banner";
import { _getProfile, _trackEvent } from "../../Helper";
import { Modal } from "bootstrap";
import { KycStatusModal } from "../../Component/Modal";
import Store from "../../Store/store";

import { _parseJwtToken, APPLE_AUTH_CONFIG } from "../../Helper/function";

//custom styles
import styles from "../../Style/Component/login.module.scss";

// Custom data
const SIGNIN_METHODS = [
    {
        id: 1,
        display_name: "Phone",
        icon: "signup-phone-icon"
    },
    {
        id: 2,
        display_name: "Email",
        icon: "singup-mail-icon"
    },
];


const Login = () => {

    const inputRef = useRef(null);
    const screenViewLatch = useRef(false);
    const otpViewLatch = useRef(false);
    const navigate = useNavigate();
    const { pathname, state } = useLocation();

    const [currentStep, setCurrentStep] = useState(1);
    const [selectedSignInMethod, setSelectedSignInMethod] = useState(1);
    const [email, setEmail] = useState("");
    const [emailError, setEmailError] = useState("");
    const [phoneNumber, setPhoneNumber] = useState(state && state.phone_number ? state.phone_number : "");
    const [phoneNumberError, setPhoneNumberError] = useState("");
    const [otpValue, setOtpValue] = useState("");
    const [focusedInput, setFocusedInput] = useState(2);
    const [timer, setTimer] = useState(0);
    const [loading, setLoading] = useState(false);
    const [disableResendOTP, setDisableResendOTP] = useState(false);
    const [selectedCountryCode, setSelectedCountryCode] = useState(state && state.country_code ? state.country_code : "+91");
    const [selectedCountryLocale, setSelectedCountryLocale] = useState(state && state.country_locale ? state.country_locale : "IN");
    const [statusModal, setStatusModal] = useState(false);
    const [isKYCRejected, setIsKYCRejected] = useState(false);

    useEffect(() => {
        if (!screenViewLatch.current) {
            screenViewLatch.current = true;
            _trackEvent("login_screen_viewed", { screen: "login" });
        }
        window.scrollTo({
            top: 0, behavior: 'smooth' //scroll top
        });
        // navigate(pathname, { replace: true });
    }, [pathname]);

    useEffect(() => {
        if (statusModal === true) {
            let kyc_status = new Modal(document.getElementById('kyc-status-modal'));
            kyc_status.show();
        }
    }, [statusModal]);

    // Fire OTP verification screen view once when entering step 2
    useEffect(() => {
        if (currentStep === 2 && !otpViewLatch.current) {
            otpViewLatch.current = true;
            _trackEvent("screen_otp_verification_viewed", { screen: "login" });
        }
    }, [currentStep]);

    //for timer
    useEffect(() => {
        if (timer > 0) {
            // Start the timer when requestOtpSuccess becomes true
            const countdown = setInterval(() => {
                setTimer((prevTimer) => prevTimer - 1);
            }, 1000);
            // Clear the timer when the component unmounts or when the timer reaches 0
            return () => {
                clearInterval(countdown);
            };
        };
    }, [timer]);

    // When redirecting from signup flow
    useEffect(() => {
        if (state !== null) {
            if (state.phone_number) {
                setPhoneNumber(state.phone_number);
                setSelectedSignInMethod(1);
            }

            if (state.email) {
                setEmail(state.email);
                setSelectedSignInMethod(2);
            }

            if (state.country_code) {
                setSelectedCountryCode(state.country_code);
            }

            if (state.country_locale) {
                setSelectedCountryLocale(state.country_locale);
            }

        }
    }, [state]);

    // handling Enter key 
    useEffect(() => {

        const _handleOnEnter = (e) => {
            if (e.key === 'Enter') {
                if (currentStep == 1) {
                    _handleGetSignInOtp();
                } else {
                    _handleVerifyOtp();
                }
            }
        };

        document.addEventListener('keydown', _handleOnEnter);

        return () => {
            document.removeEventListener('keydown', _handleOnEnter);
        };

    }, [currentStep, phoneNumber, otpValue, email]);

    // handle Signin method select
    const _handleSignInMethodSelect = (id) => {
        setSelectedSignInMethod(id);
    };

    //handle email change
    const _handleEmailChange = (value) => {
        if (!email && value) {
            _trackEvent("email_entered", { screen: "login", method: "email" });
        }
        setEmail(value.toLowerCase());
        setEmailError("");
    };

    // handle phone number change
    const _handlePhoneNumberChange = (value, selected_country_code, country_locale) => {
        if (!phoneNumber && value) {
            _trackEvent("mobile_number_entered", { screen: "login", method: "phone" });
        }
        setPhoneNumber(value);
        setPhoneNumberError("");

        if (selected_country_code) {
            setSelectedCountryCode(selected_country_code);
        }

        if (country_locale) {
            setSelectedCountryLocale(country_locale);
        }
    };

    // handle modal close
    const _handleModalClose = () => {
        setStatusModal(false);
        if (isKYCRejected) {
            // Updating store to navigate to onboarding flow
            Store.dispatch({
                type: 'UPDATE_ONBOARDING_TOKEN',
                payload: {
                    refresh_token: Cookies.get('onboarding_refresh_token'),
                    access_token: sessionStorage.getItem('access_token')
                }
            });
        } else {
            _logOut();
        }
    };

    //handle back
    const _handleBack = () => {
        setCurrentStep((cur_step) => cur_step - 1);
        setTimer(0);
    };

    // handle RESEND OTP 
    const _handleResendOTP = () => {
        let resend = true;
        _trackEvent("otp_resend_clicked", { screen: "login" });
        setDisableResendOTP(true);
        _getOTP(resend);
    };

    //handle confirm pin
    const _handleChangeOTP = (value) => {
        if (loading !== true) {
            setOtpValue(value);
        };
    };

    //handle get otp signup
    const _handleGetSignInOtp = () => {

        let validation = true;

        // valid phone number and email pattern 
        let valid_phone = /^[6-9]\d{9}$/;
        let validate_email = /^[a-zA-Z0-9._:$!%-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]+$/;

        if (selectedSignInMethod == 1) {

            // removing country code
            let phone_number = Number(phoneNumber.replace(selectedCountryCode, ""));

            // Validating phone
            if (selectedCountryCode == "+91") {
                if (!valid_phone.test(phone_number)) {
                    setPhoneNumberError("Please enter a valid 10-digit phone number");
                    validation = false;
                }
            } else {
                let is_valid_number = isValidPhoneNumber(String(phoneNumber));
                if (is_valid_number === false) {
                    setPhoneNumberError("Please enter a valid number");
                    validation = false;
                }
            }

        } else {

            // Validating email
            if (!validate_email.test(email)) {
                setEmailError("Please enter a valid email id");
                validation = false;
            }

        }

        if (validation === true) {
            setLoading(true);
            const method = selectedSignInMethod == 1 ? "phone" : "email";
            _trackEvent("login_started", { screen: "login", method });
            _trackEvent("otp_requested", { screen: "login", method });
            // API call
            _getOTP();
        }
    };

    //handle for login otp verify
    const _handleVerifyOtp = () => {
        if (otpValue.length !== 4) return;
        setLoading(true);
        _trackEvent("otp_verification_submitted", { screen: "login" });
        // API call
        _verifyOTP();
    };

    const _handleSignNavigateToUp = () => {
        _trackEvent("signup_cta_clicked", { screen: "login" });
        navigate("/signup");
    };

    const _logOut = () => {
        window.location.href = "/";
        Cookies.remove('refreshToken');
        setCurrentStep(1)
        setStatusModal(false);
    }


    // ================================ //
    //============= API CALLS ========= //

    // API - get login otp 
    const _getOTP = (resend) => {

        let api_version = 2;
        let is_post = true;
        let url = 'auth/get-login-otp';
        let data = {
            "type": "LOGIN",
            "auth_type": selectedSignInMethod == 1 ? "MOBILE" : "EMAIL",
        };

        if (selectedSignInMethod == 1) {
            data = {
                ...data,
                "country_code": selectedCountryCode,
                "phone_number": Number(phoneNumber.replace(selectedCountryCode, "")),
            }
        } else {
            data = {
                ...data,
                "email": email
            }
        };

        APIService(is_post, url, data, null, api_version).then((response) => {
            if (response.status_code === 200) {
                _trackEvent("otp_sent", { screen: "login", method: selectedSignInMethod == 1 ? "phone" : "email" });
                setCurrentStep(2); // OTP page
                setTimer(30);
                setOtpValue("");
                setFocusedInput(2);

                if (resend === true) {
                    toast.dismiss();
                    toast.success(`OTP sent successfully to registered ${selectedSignInMethod == 1 ? " Mobile" : " Email"}`, {
                        className: "e-toast",
                    });
                }

            } else {

                _trackEvent("otp_send_failed", { screen: "login", status_code: response.status_code, error_message: response.message });
                if (response.status_code === 400 && response?.data?.flow === "SIGNUP") {
                    navigate("/signup", {
                        state: {
                            phone_number: phoneNumber,
                            email: email,
                            country_code: selectedCountryCode,
                            country_locale: selectedCountryLocale
                        }
                    });

                    toast.dismiss();
                    toast.error(response.message, {
                        className: "e-toast",
                    });
                } else {
                    toast.dismiss();
                    toast.error(response.message, {
                        className: "e-toast",
                    });
                }
            }

            setLoading(false);
            setDisableResendOTP(false);
        });
    };


    // API - Verify otp
    const _verifyOTP = () => {

        let api_version = 2;
        let is_post = true;
        let url = 'auth/verify-otp';
        let data = {
            "type": "LOGIN",
        };

        if (selectedSignInMethod === 1) {
            data = {
                ...data,
                "country_code": selectedCountryCode,
                "phone_number": Number(phoneNumber.replace(selectedCountryCode, "")),
                "phone_otp": otpValue,
            }
        } else {
            data = {
                ...data,
                "email": email,
                "email_otp": otpValue,
            }
        };

        APIService(is_post, url, data, null, api_version).then((response) => {
            if (response.status_code === 200) {

                _trackEvent("otp_verified", { screen: "login" });
                _trackEvent("otp_verification_success", { screen: "login" });
                _trackEvent("login_success", { screen: "login", method: selectedSignInMethod == 1 ? "phone" : "email" });

                const { refresh_token, access_token, is_auth_configured, signup_progress_text } = response.data;

                // Check user is invested or not if not go to onboarding flow
                if (is_auth_configured && signup_progress_text === "INVESTED") {

                    toast.dismiss();
                    toast.success("OTP verified successfully", {
                        className: "e-toast",
                    });

                    // Required in unlock page 
                    Cookies.set('refresh_token', refresh_token, { domain: process.env.REACT_APP_DOMAIN, path: '/', expires: 30 });
                    navigate("/unlock", { replace: true });

                } else if (signup_progress_text === "KYC_SUBMITTED") {
                    setStatusModal(true); // KYC status modal    
                } else if (signup_progress_text === "KYC_REJECTED") {
                    setStatusModal(true); // KYC status modal   
                    setIsKYCRejected(true);
                    Cookies.set('onboarding_refresh_token', refresh_token, { domain: process.env.REACT_APP_DOMAIN, path: '/', expires: 30 });
                    sessionStorage.setItem('access_token', access_token);
                    localStorage.setItem("is_auth_configured", is_auth_configured);
                } else {
                    // User not created investment -> onboarding flow
                    Cookies.set('onboarding_refresh_token', refresh_token, { domain: process.env.REACT_APP_DOMAIN, path: '/', expires: 30 });
                    sessionStorage.setItem('access_token', access_token);
                    localStorage.setItem("is_auth_configured", is_auth_configured);
                    Store.dispatch({
                        type: 'UPDATE_ONBOARDING_TOKEN',
                        payload:
                            { refresh_token, access_token }
                    });


                    if (signup_progress_text === "ACCOUNT_CREATED") {
                        let message = is_auth_configured ? "Please make first investment to continue" : "Please set PIN to continue";
                        toast.info(message, {
                            className: "e-toast",
                        });

                    } else {
                        toast.dismiss();
                        toast.success("Please complete account creation to continue ", {
                            className: "e-toast",
                        });
                    }
                }

            } else {
                _trackEvent("otp_verification_failed", { screen: "login", status_code: response.status_code, error_message: response.message });
                _trackEvent("login_failed", { screen: "login" });
                toast.dismiss();
                toast.error(response.message, {
                    type: "error",
                    className: "e-toast",

                });

                setOtpValue("");
                setFocusedInput(2);
            }
            setLoading(false);
        });
    };


    const _handleGoogleLogin = async () => {
        _trackEvent("google_signup_clicked", { screen: "login", method: "google" });
        try {
            await signOut(auth); // 👈 clear previous session

            const provider = new GoogleAuthProvider();
            provider.addScope("email");
            provider.addScope("profile");

            const result = await signInWithPopup(auth, provider);
            const user = result.user;
            const idToken = await user.getIdToken();

            console.log("Firebase ID Token:", idToken);

            const url = 'auth/google-signin';
            let payload = {
                idToken,
                user,
            }
            const api_version = 2
            // Send token to backend
            APIService(true, url, payload, null, api_version).then((response) => {
                if (response.status_code === 200) {
                    _trackEvent("google_signup_success", { screen: "login", method: "google" });
                    _trackEvent("login_success", { screen: "login", method: "google" });
                    const { refresh_token, access_token, is_auth_configured, signup_progress_text } = response.data;

                    // Check user is invested or not if not go to onboarding flow
                    if (is_auth_configured && signup_progress_text === "INVESTED") {

                        toast.dismiss();
                        toast.success("OTP verified successfully", {
                            className: "e-toast",
                        });

                        // Required in unlock page 
                        Cookies.set('refresh_token', refresh_token, { domain: process.env.REACT_APP_DOMAIN, path: '/', expires: 30 });
                        navigate("/unlock", { replace: true });

                    } else if (signup_progress_text === "KYC_SUBMITTED") {
                        setStatusModal(true); // KYC status modal    
                    } else if (signup_progress_text === "KYC_REJECTED") {
                        setStatusModal(true); // KYC status modal   
                        setIsKYCRejected(true);
                        Cookies.set('onboarding_refresh_token', refresh_token, { domain: process.env.REACT_APP_DOMAIN, path: '/', expires: 30 });
                        sessionStorage.setItem('access_token', access_token);
                        localStorage.setItem("is_auth_configured", is_auth_configured);
                    } else {
                        // User not created investment -> onboarding flow
                        Cookies.set('onboarding_refresh_token', refresh_token, { domain: process.env.REACT_APP_DOMAIN, path: '/', expires: 30 });
                        sessionStorage.setItem('access_token', access_token);
                        localStorage.setItem("is_auth_configured", is_auth_configured);
                        Store.dispatch({
                            type: 'UPDATE_ONBOARDING_TOKEN',
                            payload:
                                { refresh_token, access_token }
                        });


                        if (signup_progress_text === "ACCOUNT_CREATED") {
                            let message = is_auth_configured ? "Please make first investment to continue" : "Please set PIN to continue";
                            toast.info(message, {
                                className: "e-toast",
                            });

                        } else {
                            toast.dismiss();
                            toast.success("Please complete account creation to continue ", {
                                className: "e-toast",
                            });
                        }
                    }
                } else {
                    toast.error(response.message)
                }
            })

        } catch (error) {
            console.error("Google sign-in error:", error);
        }
    };


    return (
        <>
            <section className="e-login-gradient">
                {/* header  */}
                <Header />
                {/* login  */}
                <div className="col-sm-10 col-md-10 col-lg-8 col-xl-7 mx-auto px-md-0">
                    <div className="e-padding-block-40px-res padding-76px-top padding-150px-bottom position-relative">
                        <div className={`${styles.e_onboarding_wrapper} e-border-radius-24`}>
                            <div className="row justify-content-between">
                                <AuthSlider />
                                <div className="col-md-6 col-12 position-relative gradient-vr-line">


                                    {
                                        // step-1 =>  Login  method select
                                        currentStep === 1 ?
                                            <div className="margin-36px-right no-margin-res-md">
                                                <h3 className="d-flex gap-12px align-items-center e-text-dark-charcoal e-alt-font-poppins e-font-weight-400 e-font-14 line-height-22px e-text-charleston-green ">
                                                    Login with Phone or Email
                                                    {/* gradient line */}
                                                    <span className="gradient-border-line flex-fill"></span>
                                                </h3>

                                                <div className="d-flex gap-3 my-4 my-sm-3 pb-2">
                                                    <RadioGroup
                                                        type={3}
                                                        data={SIGNIN_METHODS}
                                                        selected={selectedSignInMethod}
                                                        handleSelect={_handleSignInMethodSelect} />
                                                </div>

                                                {
                                                    selectedSignInMethod == 2 ?
                                                        <div className=' mb-1'>
                                                            <CustomTextInput label="Email"
                                                                placeholder="josephthomas@gmail.com"
                                                                type="text"
                                                                value={email}
                                                                require
                                                                floatingLabel
                                                                errorMsg={emailError}
                                                                handleChange={_handleEmailChange} />
                                                        </div>
                                                        :
                                                        <>
                                                            <div className="mb-1">
                                                                <CustomPhoneInput label="Phone Number"
                                                                    id="login-phone-input"
                                                                    placeholder="9876543210"
                                                                    className={phoneNumber ? `e-number-input-padding w-100 ` : `e-number-input-padding w-100`}
                                                                    type="text"
                                                                    floatingLabel
                                                                    value={phoneNumber}
                                                                    selectedCountryCode={selectedCountryCode}
                                                                    selectedCountryLocale={selectedCountryLocale}
                                                                    require
                                                                    error={phoneNumber && phoneNumberError}
                                                                    handleChange={_handlePhoneNumberChange} />
                                                            </div>
                                                        </>
                                                }

                                                <PrimaryButton name={`Get OTP`}
                                                    loading={loading}
                                                    disabled={loading}
                                                    inActive={((selectedSignInMethod == 2 && email.length !== 0) || (selectedSignInMethod == 1 && phoneNumber)) ? false : true}
                                                    className={`w-100 padding-12px-tb e-font-16 mt-4`}
                                                    onPress={_handleGetSignInOtp} />


                                                <div className="mt-4">
                                                    <div className="d-flex align-items-center gap-18px">
                                                        <span className="gradient-border-line flex-fill transform-rotate-180"></span>
                                                        <span className="e-text-gray e-alt-font-poppins e-font-weight-500 e-font-12-res e-font-16 line-height-18px ">
                                                            Or Continue with
                                                        </span>
                                                        <span className="gradient-border-line flex-fill"></span>
                                                    </div>
                                                    <div className="mt-3 d-flex justify-content-center align-items-center gap-2">
                                                        <button className="social-login-button flex-fill" onClick={_handleGoogleLogin}>
                                                            <Icon icon="google_icon"
                                                                className="cursor-pointer w-24px width-20px-res"
                                                                onClick={() => { }} />
                                                            Google
                                                        </button>
                                                        {/* <AppleSignin
                                                            authOptions={APPLE_AUTH_CONFIG}
                                                            onSuccess={() =>{}} //_handleAppleLoginSuccess
                                                            onError={()=>{}} //_handleAppleLoginError
                                                            render={(props) => <button {...props} className="social-login-button flex-fill">
                                                                <Icon icon="apple-icon"
                                                                    className="cursor-pointer w-24px width-20px-res"
                                                                    onClick={() => { }} />
                                                                Apple
                                                            </button>}
                                                        /> */}
                                                    </div>
                                                </div>



                                                <div className="e-text-dark-liver-70per e-font-14 e-alt-font-poppins e-font-weight-400 line-height-22px text-center mt-3">
                                                    Don't have an account?
                                                    <span className="cursor-pointer text-decoration-hover-underline e-text-egyptian-blue ps-1 e-font-weight-500" onClick={_handleSignNavigateToUp}>
                                                        Signup
                                                    </span>
                                                </div>

                                                <div className="mt-3">
                                                    <span className="invisible">
                                                        or
                                                    </span>
                                                    <div className="invisible my-3 d-flex justify-content-center align-items-center">

                                                        <Icon icon="back-icon"
                                                            className="cursor-pointer"
                                                            size={32}
                                                            onClick={_handleBack} />
                                                        <Icon icon="back-icon"
                                                            className="cursor-pointer"
                                                            size={32}
                                                            onClick={_handleBack} />

                                                    </div>

                                                    <p className="text-center e-text-dark-liver-70per e-font-12 e-alt-font-poppins e-font-weight-400 line-height-18px">
                                                        By continuing, I agree with
                                                        <a href="https://indexfundssahihai.com/terms"
                                                            target="_blank"
                                                            onClick={() => _trackEvent("terms_and_conditions_clicked", { screen: "login", type: "terms" })}
                                                            className="px-1 hover-blue-link">
                                                            T&C
                                                        </a>
                                                        {","}
                                                        <a href="https://indexfundssahihai.com/privacy-policy"
                                                            target="_blank"
                                                            onClick={() => _trackEvent("terms_and_conditions_clicked", { screen: "login", type: "privacy" })}
                                                            className="px-1 hover-blue-link">
                                                            Privacy Policy
                                                        </a>
                                                    </p>
                                                </div>

                                            </div>
                                            :

                                            // OTP verification
                                            currentStep === 2 ?
                                                <div className="py-sm-4 my-sm-2 margin-36px-right no-margin-res-md">
                                                    <Icon icon="back-icon"
                                                        className="cursor-pointer mb-3"
                                                        size={32}
                                                        onClick={_handleBack} />

                                                    <h3 className='e-text-dark-jungle-green e-font-20  e-alt-font-poppins e-font-weight-600 line-height-32px letter-spacing-neg-2per'>
                                                        {
                                                            selectedSignInMethod == 1 ? "OTP Verification" : "Verify email"
                                                        }
                                                    </h3>

                                                    {
                                                        String(phoneNumber).startsWith("+91") ?
                                                            <div className='e-text-onyx-80per e-font-14 e-alt-font-poppins e-font-weight-400 line-height-24px mb-4'>
                                                                Enter the 4-digit OTP received on your {selectedSignInMethod == 1 ? " Mobile number " : " Email "}
                                                                <span className="d-inline-flex gap-1 e-text-chinese-black e-font-weight-600 cursor-pointer">
                                                                    {selectedSignInMethod == 1 ? ` ${phoneNumber}` : ` ${email}`}
                                                                    <Icon icon="edit-data-icon"
                                                                        onClick={() => { _trackEvent("edit_contact_details_clicked", { screen: "login" }); setCurrentStep(1); }}
                                                                        size={20} />
                                                                </span>
                                                            </div>
                                                            :
                                                            <div className='e-text-onyx-80per e-font-14 e-alt-font-poppins e-font-weight-400 line-height-24px mb-4'>
                                                                Enter the 4-digit OTP received on your Email
                                                                {
                                                                    email &&
                                                                    <span className="d-inline-flex gap-1 e-text-chinese-black e-font-weight-600 cursor-pointer ps-1">
                                                                        {email}
                                                                        <Icon icon="edit-data-icon"
                                                                            onClick={() => { _trackEvent("edit_contact_details_clicked", { screen: "login" }); setCurrentStep(1); }}
                                                                            size={20} />
                                                                    </span>
                                                                }
                                                            </div>
                                                    }

                                                    <OTPInput ref={inputRef}
                                                        id="otp"
                                                        value={otpValue}
                                                        spellcheck="false"
                                                        focused_input={focusedInput}
                                                        onFocus={() => setFocusedInput(2)}
                                                        onBlur={() => setFocusedInput(0)}
                                                        onChange={_handleChangeOTP} />

                                                    <div className='e-inline-flex flex-wrap pt-2 e-text-charleston-green gap-2 e-font-14 e-alt-font-poppins line-height-18px'>
                                                        <span className="e-font-weight-500 ">
                                                            Didn't get the OTP?
                                                        </span>
                                                        <button className={`${timer !== 0 ? "e-disable e-text-dark-liver" : "e-text-egyptian-blue"} px-0 d-flex gap-1 border-0 e-bg-transparent e-font-weight-500`}
                                                            disabled={disableResendOTP}
                                                            onClick={_handleResendOTP} >
                                                            Resend OTP
                                                            {timer !== 0 &&
                                                                <p className='mb-0 e-text-dark-liver e-alt-font-poppins e-font-weight-500'>
                                                                    in {timer} {timer == 1 ? "second" : "seconds"}
                                                                </p>
                                                            }
                                                        </button>

                                                    </div>

                                                    <div className='mt-4'>
                                                        {otpValue.length >= 4 ?
                                                            <PrimaryButton
                                                                name={"Continue"}
                                                                loading={loading}
                                                                disabled={loading}
                                                                className="w-100 padding-12px-top padding-12px-bottom e-font-16"
                                                                onPress={_handleVerifyOtp} />
                                                            :
                                                            <PrimaryButton
                                                                name="Continue"
                                                                disabled={true}
                                                                inActive={true}
                                                                className="w-100 padding-12px-top padding-12px-bottom e-bg-bright-gray e-font-16 e-cursor-none" />
                                                        }
                                                    </div>
                                                </div>
                                                :
                                                null
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* footer section  */}
            <div className="e-bg-anti-flash-white">
                <Footer auth={true} />
            </div>

            {/* kyc status modal  */}
            {
                statusModal === true &&
                <KycStatusModal title={isKYCRejected ? "Submission Failed" : "Submitted"}
                    type={2}
                    image={isKYCRejected ? require("../../Assets/Images/KYC/kyc-failure.png") : require("../../Assets/Images/KYC/kyc-success.webp")}
                    buttonName={isKYCRejected ? "Retry now" : "Understood"}
                    description={
                        isKYCRejected ?
                            "We couldn’t complete your account creation process due to a verification or system issue. Please review your details or try submitting again."
                            :
                            "The account creation process has been initiated and the process will take 1-3 business days time. We will notify over mail once it is completed."
                    }
                    onCloseModal={_logOut}
                    onButtonClick={_handleModalClose}
                />
            }
        </>
    )
}
export default Login;